"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { useCart, useCartActions, useCartUpdatedAt } from "@/tools/medusa/store";
import { reconcileChildHolds } from "@/tools/medusa/services/sponsorshipHolds";
import { childHoldExpiries, lapsedChildHolds } from "@/tools/medusa/helpers";

/**
 * Keeps a sponsored child reserved for as long as the supporter is still
 * using the site, and takes them out of the cart once they're gone.
 *
 * The gateway releases a child an hour after they're reserved. That's the
 * right behaviour for someone who wandered off, and the wrong behaviour for
 * someone still shopping — so we treat these as being "still here" and push
 * the expiry out when any of them happens:
 *
 *   - moving to another page
 *   - opening the cart, either the drawer or /cart
 *   - changing the cart: adding, removing, or re-cadencing a line
 *   - coming back to the tab
 *
 * The last two exist because the first two only fire on arrival. Someone who
 * settles on one page — the cart itself, or a long checkout form — generated
 * no signal at all, so their hold could lapse while they were demonstrably
 * still working. Cart edits key off `cartUpdatedAt`, which the supporter's own
 * mutations bump and background hold renewals deliberately don't, so this
 * can't feed itself.
 *
 * None of them triggers a request on its own. We only call the gateway when a
 * child's hold is within `HOLD_RENEW_THRESHOLD_MS` of running out, so a
 * browsing session costs about one request per child every 50 minutes.
 * Someone who stops interacting stops renewing, and the child is released on
 * the gateway's own schedule — an hour after their last action, which is the
 * inactivity window requirement 1.5.2.1 asks for.
 *
 * Renders nothing, and does nothing at all for carts with no children in them.
 */
const SponsorshipHoldKeeper = () => {
  const pathname = usePathname();
  const { items, hasHydrated, isCartOpen } = useCart();
  const cartUpdatedAt = useCartUpdatedAt();
  const { updateItemMetadata, removeItem, recordRemovedSponsorship } = useCartActions();

  // Actions come off a Zustand store that hands back new object identities on
  // every cart change. Holding them in a ref keeps them out of the effect's
  // dependencies, so the effect fires on the activity signals below — not
  // every time the cart's contents shift.
  const actionsRef = useRef({ updateItemMetadata, removeItem, recordRemovedSponsorship });
  actionsRef.current = { updateItemMetadata, removeItem, recordRemovedSponsorship };

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const hasHydratedRef = useRef(hasHydrated);
  hasHydratedRef.current = hasHydrated;

  const reconcile = useCallback(() => {
    if (!hasHydratedRef.current) {
      return;
    }
    const currentItems = itemsRef.current;
    if (currentItems.length === 0) {
      return;
    }
    void reconcileChildHolds(currentItems, actionsRef.current);
  }, []);

  useEffect(() => {
    reconcile();
    // `pathname` covers navigation; `isCartOpen` covers the drawer. The cart
    // page contributes its own mount through the pathname change, and
    // `cartUpdatedAt` covers edits made while sitting on one of them.
  }, [pathname, isCartOpen, hasHydrated, cartUpdatedAt, reconcile]);

  useEffect(() => {
    // Returning to the tab is both an activity signal and the moment the
    // countdown is most likely to be stale: a backgrounded tab has its timers
    // throttled, so whatever it was showing may be minutes out of date.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        reconcile();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [reconcile]);

  // The soonest deadline in the cart, which is the only one worth a timer.
  // Handling it re-renders with a new set, so the next one is scheduled then.
  const nextExpiryAt = childHoldExpiries(items)[0]?.expiresAt;

  useEffect(() => {
    if (!(hasHydrated && nextExpiryAt !== undefined)) {
      return;
    }

    /**
     * Give up a lapsed child without asking the gateway.
     *
     * Every other path here defers to the gateway, but there's no read-only
     * question to ask: `reserve` and `availability` both extend the hold, so
     * checking at zero would renew it and a tab left open would hold a child
     * forever — the opposite of the inactivity release in 1.5.2.1. Acting
     * locally is safe in this one direction because it only ever releases;
     * the gateway's own hour is up at the same moment.
     *
     * Without this, a supporter sitting still watched a row say "no longer
     * reserved" indefinitely with the line still priced into their subtotal,
     * and only found out on their next navigation.
     */
    const releaseLapsedHolds = () => {
      const lapsed = lapsedChildHolds(itemsRef.current);
      for (const childId of lapsed) {
        const lines = itemsRef.current.filter((item) => item.metadata?.child_id === childId);
        void (async () => {
          try {
            for (const line of lines) {
              // The loop covers the whole group, so the store mustn't cascade
              // off the sponsorship line underneath it.
              await actionsRef.current.removeItem(line.id, { cascadeGroup: false });
            }
          } catch (error) {
            // Say nothing if the lines are still there — the next reconcile
            // will deal with them, and a notice about a cart that didn't
            // change is worse than a late one.
            console.warn(`[holds] could not release lapsed hold for ${childId}`, error);
            return;
          }
          actionsRef.current.recordRemovedSponsorship({ child_id: childId, reason: "expired" });
        })();
      }
    };

    const timeout = setTimeout(releaseLapsedHolds, Math.max(0, nextExpiryAt - Date.now()));
    return () => clearTimeout(timeout);
  }, [nextExpiryAt, hasHydrated]);

  return null;
};

export default SponsorshipHoldKeeper;
